import React, { useMemo } from 'react';
import { VBox } from '../layout';
import { isNullOrUndefined } from '../../util';
import { LineChartTooltip } from './LineChartTooltip';
import type { LineChartProps, SingleLineData } from './LineChartUtil';
import {
  d3,
  getChartSvgProps,
  LineChartHeader,
  margin,
  SharedLineChartSetup,
  useChartLayoutEffect,
  useChartRefs,
  useScaledXAxis,
  useTooltipHandler,
  useTrackChartDimensions
} from './LineChartUtil';

export type SingleLineChartProps = LineChartProps & {
  data: SingleLineData[];
};

export const SingleLineChart = ({
  data,
  header,
  headerModalTitle,
  headerModalContent,
  initialWidth = 400,
  initialHeight = 250
}: Readonly<SingleLineChartProps>): JSX.Element => {
  const { legendRef, dotsGroupRef, svgContainerRef } = useChartRefs();

  const { width, height } = useTrackChartDimensions(
    svgContainerRef,
    initialWidth,
    initialHeight
  );

  const x = useScaledXAxis(data, width);

  const y = useMemo(
    () =>
      d3
        .scaleLinear()
        .domain([
          (d3.min(data, d => d.y.firstLine.value) as number) * 0.9,
          (d3.max(data, d => d.y.firstLine.value) as number) * 1.1
        ])
        .range([height - margin.bottom, margin.top]),
    [data, height]
  );

  const { tooltipState, showTooltip, hideTooltip } = useTooltipHandler(
    data,
    dotsGroupRef,
    x,
    y,
    width
  );

  const line = useMemo(
    () =>
      d3
        .line<SingleLineData>()
        .defined(
          d =>
            !isNullOrUndefined(d.x) && !isNullOrUndefined(d.y.firstLine.value)
        )
        .x(d => x(new Date(d.x).valueOf()) as number)
        .y(d => y(d.y.firstLine.value) as number),
    [x, y]
  );

  useChartLayoutEffect(dotsGroupRef, showTooltip, hideTooltip);

  const filteredData = useMemo(() => {
    return data.filter(
      d => !isNullOrUndefined(d.x) && !isNullOrUndefined(d.y.firstLine.value)
    );
  }, [data]);

  return (
    <VBox className="line-chart-container" ref={svgContainerRef}>
      <LineChartHeader
        header={header}
        headerModalTitle={headerModalTitle}
        headerModalContent={headerModalContent}
      />
      <svg {...getChartSvgProps(width, height)}>
        <SharedLineChartSetup
          data={data}
          x={x}
          y={y}
          height={height}
          width={width}
          firstLineLegend={header}
          legendRef={legendRef}
        />
        <g>
          <path
            transform={`translate(${margin.left}, 0)`}
            className="line"
            d={line(filteredData) ?? undefined}
          />
          <g ref={dotsGroupRef}>
            {data.map(d => (
              <>
                {!isNullOrUndefined(d.x) && (
                  <circle
                    cx={x(new Date(d.x).valueOf())}
                    cy={y(d.y.firstLine.value)}
                    r={5}
                    fill="var(--secondary-color)"
                    transform={`translate(${margin.left}, 0)`}
                  />
                )}
              </>
            ))}
          </g>
        </g>
        <LineChartTooltip
          {...tooltipState}
          firstLineLegend={header}
          showHoverGuidelines={true}
          width={width}
          height={height}
        />
      </svg>
    </VBox>
  );
};
